import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import * as userService from '../../services/users';
import { FiUsers, FiSearch, FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi';

const Users = () => {
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    search: '',
    role: '',
    isActive: '',
    page: 1,
    limit: 20,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    department: '',
    position: '',
  });

  const { data, isLoading, isError, refetch } = useQuery(
    ['users', filters],
    () => userService.getUsers(filters),
    {
      keepPreviousData: true,
    }
  );

  const users = data?.users || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  const createUserMutation = useMutation(
    (payload) => userService.createUser(payload),
    {
      onSuccess: () => {
        toast.success('User created successfully');
        queryClient.invalidateQueries('users');
        setFormData({
          name: '',
          email: '',
          password: '',
          role: 'staff',
          department: '',
          position: '',
        });
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to create user');
      },
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, isActive }) => userService.updateUserStatus(id, isActive),
    {
      onSuccess: () => {
        toast.success('User status updated');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to update user status');
      },
    }
  );

  const updateRoleMutation = useMutation(
    ({ id, role }) => userService.updateUserRole(id, role),
    {
      onSuccess: () => {
        toast.success('User role updated');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to update user role');
      },
    }
  );

  const deleteUserMutation = useMutation(
    (id) => userService.deleteUser(id),
    {
      onSuccess: () => {
        toast.success('User deleted');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete user');
      },
    }
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handleCreateUser = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Name, email and password are required');
      return;
    }

    createUserMutation.mutate(formData);
  };

  const handleToggleStatus = (user) => {
    updateStatusMutation.mutate({
      id: user._id,
      isActive: !user.isActive,
    });
  };

  const handleChangeRole = (user, role) => {
    if (role === user.role) {
      return;
    }

    updateRoleMutation.mutate({
      id: user._id,
      role,
    });
  };

  const handleDeleteUser = (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      return;
    }

    deleteUserMutation.mutate(user._id);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Users Management</h1>
          <p>Manage system users, roles and access</p>
        </div>
        <button
          className="btn btn-outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <FiRefreshCw />
          Refresh
        </button>
      </div>

      <div className="page-content">
        <div className="users-layout">
          <div className="users-main">
            <div className="users-filters">
              <div className="search-box">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search by name, email, employee ID..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
              <select
                className="form-control"
                value={filters.role}
                onChange={(e) => handleFilterChange('role', e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
                <option value="viewer">Viewer</option>
              </select>
              <select
                className="form-control"
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div className="users-table-wrapper">
              {isLoading ? (
                <div className="loading-placeholder">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="skeleton-row" />
                  ))}
                </div>
              ) : isError ? (
                <div className="error-state">
                  <p>Failed to load users.</p>
                </div>
              ) : users.length === 0 ? (
                <div className="empty-state">
                  <FiUsers size={32} />
                  <p>No users found</p>
                </div>
              ) : (
                <table className="table users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <motion.tr
                        key={user._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} />
                              ) : (
                                <span>{user.name?.charAt(0)}</span>
                              )}
                            </div>
                            <div className="user-info">
                              <div className="user-name">{user.name}</div>
                              {user.employeeId && (
                                <div className="user-meta">
                                  ID: {user.employeeId}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <select
                            className="form-control form-control-sm"
                            value={user.role}
                            onChange={(e) =>
                              handleChangeRole(user, e.target.value)
                            }
                            disabled={updateRoleMutation.isLoading}
                          >
                            <option value="super_admin">Super Admin</option>
                            <option value="admin">Admin</option>
                            <option value="manager">Manager</option>
                            <option value="staff">Staff</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        </td>
                        <td>{user.department || '-'}</td>
                        <td>
                          <button
                            className={
                              user.isActive
                                ? 'btn btn-sm btn-success'
                                : 'btn btn-sm btn-outline'
                            }
                            onClick={() => handleToggleStatus(user)}
                            disabled={updateStatusMutation.isLoading}
                          >
                            {user.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : '-'}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteUser(user)}
                            disabled={deleteUserMutation.isLoading}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => handlePageChange(filters.page - 1)}
                  disabled={filters.page === 1}
                >
                  Previous
                </button>
                <span>
                  Page {filters.page} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline"
                  onClick={() => handlePageChange(filters.page + 1)}
                  disabled={filters.page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          <div className="users-sidebar">
            <div className="card">
              <h3>Create User</h3>
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    className="form-control"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        role: e.target.value,
                      }))
                    }
                  >
                    <option value="staff">Staff</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.department}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        department: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Position</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.position}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        position: e.target.value,
                      }))
                    }
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createUserMutation.isLoading}
                >
                  <FiPlus />
                  {createUserMutation.isLoading ? 'Creating...' : 'Create User'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;
